import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import QRService from '../services/qrService'
import { useToast } from 'vue-toastification'

export const useQRStore = defineStore('qr', () => {
    const toast = useToast()

    // State
    const currentQR = ref(null)
    const isGenerating = ref(false)
    const generationHistory = ref([])
    const vcardValidationErrors = ref({}) // field-keyed errors from backend 400
    const sessionId = ref(null)
    const analytics = ref(null)
    const usageAnalytics = ref(null)
    const isLoadingAnalytics = ref(false)
    const serviceStatus = ref(null)

    // Enhanced QR Options with hybrid support
    const qrOptions = ref({
        size: 300,
        margin: 10,
        errorCorrectionLevel: 'M',
        darkColor: '#000000',
        lightColor: '#ffffff',
        format: 'png',
        source: 'auto' // 'auto', 'frontend', 'backend'
    })

    // Enhanced Style Options for qr-code-styling compatibility
    const styleOptions = ref({
        enabled: false,
        preset: null,

        // qr-code-styling options
        dotsType: 'square',
        cornersSquareType: 'square',
        cornersDotType: 'square',

        // Colors
        darkColor: null,
        lightColor: null,
        backgroundColor: null,

        // Gradients
        gradientColors: [],
        gradientType: 'linear',
        gradientRotation: 0,

        // Legacy support
        type: null,
        dotScale: 0.8,
        cornerRadius: 20,
        iconStyle: 'square',
        iconScale: 0.4,
        logoScale: 0.4,
        background: null
    })

    // Generation preferences
    const preferences = ref({
        preferFrontend: true,
        autoSave: true,
        instantPreview: true,
        fallbackToBackend: true,
        batchThreshold: 10 // Use frontend for batches smaller than this
    })

    // Form Data
    const formData = ref({
        activeTab: 'text',
        text: '',
        contact: {
            name: '',
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            organization: '',
            website: '',
            address: '',
            title: ''
        },
        wifi: {
            ssid: '',
            password: '',
            security: 'WPA',
            hidden: false
        },
        url: '',
        sms: {
            number: '',
            message: ''
        },
        email: {
            address: '',
            subject: '',
            body: ''
        }
    })

    const logoFile = ref(null)
    const iconFile = ref(null)
    const batchItems = ref([])

    // Computed
    const hasContent = computed(() => {
        switch (formData.value.activeTab) {
            case 'text':
                return formData.value.text.trim().length > 0
            case 'vcard':
                return formData.value.contact.name.trim().length > 0 ||
                    formData.value.contact.firstName.trim().length > 0
            case 'wifi':
                return formData.value.wifi.ssid.trim().length > 0
            case 'url':
                return formData.value.url.trim().length > 0
            case 'sms':
                return formData.value.sms.number.trim().length > 0
            case 'email':
                return formData.value.email.address.trim().length > 0
            case 'batch':
                return batchItems.value.length > 0
            default:
                return false
        }
    })

    const canGenerate = computed(() => {
        return hasContent.value && !isGenerating.value
    })

    const currentStyle = computed(() => {
        if (!styleOptions.value.enabled) return null

        const style = {}

        // Handle preset
        if (styleOptions.value.preset) {
            style.preset = styleOptions.value.preset
        }

        // qr-code-styling options
        if (styleOptions.value.dotsType !== 'square') {
            style.dotsType = styleOptions.value.dotsType
        }
        if (styleOptions.value.cornersSquareType !== 'square') {
            style.cornersSquareType = styleOptions.value.cornersSquareType
        }
        if (styleOptions.value.cornersDotType !== 'square') {
            style.cornersDotType = styleOptions.value.cornersDotType
        }

        // Colors
        if (styleOptions.value.darkColor) {
            style.darkColor = styleOptions.value.darkColor
        }
        if (styleOptions.value.lightColor) {
            style.lightColor = styleOptions.value.lightColor
        }
        if (styleOptions.value.backgroundColor) {
            style.backgroundColor = styleOptions.value.backgroundColor
        }

        // Gradients
        if (styleOptions.value.gradientColors && styleOptions.value.gradientColors.length > 0) {
            style.gradientColors = styleOptions.value.gradientColors
            style.gradientType = styleOptions.value.gradientType
            style.gradientRotation = styleOptions.value.gradientRotation
        }

        // Legacy support
        if (styleOptions.value.type) {
            style.type = styleOptions.value.type

            if (styleOptions.value.type === 'dots') {
                style.dotScale = styleOptions.value.dotScale
            } else if (styleOptions.value.type === 'rounded') {
                style.cornerRadius = styleOptions.value.cornerRadius
            }
        }

        // Icon/Logo options
        if (iconFile.value || logoFile.value) {
            style.iconStyle = styleOptions.value.iconStyle
            style.iconScale = styleOptions.value.iconScale
            style.logoScale = styleOptions.value.logoScale
        }

        return style
    })

    const shouldUseFrontend = computed(() => {
        if (qrOptions.value.source === 'backend') return false
        if (qrOptions.value.source === 'frontend') return true

        // Auto mode logic
        if (!preferences.value.preferFrontend) return false
        if (logoFile.value || iconFile.value) return false
        if (qrOptions.value.format === 'svg') return false

        return true
    })

    const availablePresets = computed(() => {
        return QRService.getPresetStyles ? Object.keys(QRService.getPresetStyles()) : []
    })

    const generationSource = computed(() => {
        if (logoFile.value || iconFile.value) return 'backend'
        if (formData.value.activeTab === 'batch' && batchItems.value.length > preferences.value.batchThreshold) return 'backend'
        // vCard and WiFi always use backend; badge must reflect that
        if (formData.value.activeTab === 'vcard' || formData.value.activeTab === 'wifi') return 'backend'
        return shouldUseFrontend.value ? 'frontend' : 'backend'
    })

    // Actions
    const initializeSession = () => {
        let sessionIdValue = localStorage.getItem('sessionId')
        if (!sessionIdValue) {
            sessionIdValue = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
            localStorage.setItem('sessionId', sessionIdValue)
        }
        sessionId.value = sessionIdValue
        loadPreferences()
    }

    const generatePreview = async () => {
        if (!canGenerate.value) return

        isGenerating.value = true

        try {
            let result
            const content = getContentForActiveTab()
            const options = { ...qrOptions.value }
            const style = currentStyle.value

            if (shouldUseFrontend.value && preferences.value.instantPreview) {
                result = await QRService.generateStyledFrontend(content, style, options)
            }
        }
        catch (error) {
            console.error('Preview generation error:', error)
            toast.error(error.message || 'Failed to generate preview')
            throw error
        } finally {
            isGenerating.value = false
        }
    }

    const generateQR = async () => {
        if (!canGenerate.value) return

        isGenerating.value = true
        vcardValidationErrors.value = {}
        try {
            let result
            const content = getContentForActiveTab()
            const options = { ...qrOptions.value }
            const style = currentStyle.value

            // Smart generation routing
            if (styleOptions.value.preset) {
                // Preset generation
                if (iconFile.value || logoFile.value) {
                    result = await QRService.generateStyledWithIcon(
                        content,
                        iconFile.value || logoFile.value,
                        style,
                        options
                    )
                } else {
                    result = await QRService.generateWithPreset(content, styleOptions.value.preset, options)
                }
            } else if (styleOptions.value.enabled && style) {
                // Styled generation with smart routing
                if (iconFile.value || logoFile.value) {
                    // Always use backend for logo/icon processing
                    result = await QRService.generateStyledWithIcon(
                        content,
                        iconFile.value || logoFile.value,
                        style,
                        options
                    )
                } else {
                    // Smart routing for styling
                    switch (formData.value.activeTab) {
                        case 'vcard':
                            result = await QRService.generateVCard(formData.value.contact, options, style)
                            break
                        case 'wifi':
                            result = await QRService.generateWiFi(formData.value.wifi, options, style)
                            break
                        default:
                            if (shouldUseFrontend.value && preferences.value.instantPreview) {
                                result = await QRService.generateStyledFrontend(content, style, options)
                            }
                            else {
                                result = await QRService.generateSmart(content, style, options)
                            }
                    }
                }
            } else if (logoFile.value) {
                // Logo-only generation
                result = await QRService.generateWithLogo(content, logoFile.value, {
                    ...options,
                    logoScale: styleOptions.value.logoScale
                })

            } else if (iconFile.value) {
                // Icon-only generation
                console.warn('Icon generation is not yet implemented in this version')
                // Uncomment when QRService supports icon generation
                // result = await QRService.generateWithIcon(content, iconFile.value, {
                //     ...options,
                //     iconScale: styleOptions.value.iconScale,
                //     iconStyle: styleOptions.value.iconStyle
                // })

                throw new Error('Icon generation is not yet implemented in this version')
            } else {
                // Basic generation
                switch (formData.value.activeTab) {
                    case 'vcard':
                        result = await QRService.generateVCard(formData.value.contact, options)
                        break
                    case 'wifi':
                        result = await QRService.generateWiFi(formData.value.wifi, options)
                        break
                    default:
                        if (shouldUseFrontend.value) {
                            result = await QRService.generateStyledFrontend(content, {}, options)
                        } else {
                            result = await QRService.generate(content, options)
                        }
                }
            }

            // Handle different response formats; ensure contentType for Generation Info
            const qrData = result
            console.log('QR generation result:', qrData)
            currentQR.value = { ...qrData, contentType: qrData.contentType ?? formData.value.activeTab }

            if (preferences.value.autoSave) {
                addToHistory(result)
            }

            const source = result.source || generationSource.value
            toast.success(`QR code generated successfully! (${source})`)

            return qrData

        } catch (error) {
            console.error('Generation error:', error)
            const isTimeout = error.code === 'ECONNABORTED' || (error.message && String(error.message).toLowerCase().includes('timeout'))
            const tab = formData.value.activeTab
            if (isTimeout && (tab === 'vcard' || tab === 'wifi')) {
                toast.error(tab === 'vcard'
                    ? 'Contact QR timed out. Try again or check your connection.'
                    : 'WiFi QR timed out. Try again or check your connection.')
            } else {
                toast.error(error.message || 'Failed to generate QR code')
            }
            if (error.details && Array.isArray(error.details)) {
                const byField = {}
                error.details.forEach((d) => {
                    const key = (d.param || d.path || d.field || '').replace(/^contact\./, '')
                    if (key) byField[key] = d.msg || d.message || 'Invalid value'
                })
                vcardValidationErrors.value = byField
            } else {
                vcardValidationErrors.value = {}
            }
            throw error
        } finally {
            isGenerating.value = false
        }
    }

    const generateBatch = async () => {
        if (batchItems.value.length === 0) {
            toast.error('No items in batch')
            return
        }

        isGenerating.value = true
        try {
            let result

            // Smart batch routing
            const useSmartBatch = batchItems.value.length <= preferences.value.batchThreshold &&
                batchItems.value.every(item => !item.logo && !item.icon)

            if (useSmartBatch) {
                result = await QRService.generateSmartBatch(batchItems.value, qrOptions.value)
            } else {
                result = await QRService.generateBatch(batchItems.value, qrOptions.value)
            }

            // Add successful items to history
            if (result.qrCodes) {
                result.qrCodes.forEach(item => {
                    if (item.qrCode) {
                        addToHistory(item)
                    }
                })
            } else if (result.data?.results) {
                result.data.results.forEach(item => {
                    if (item.success) {
                        addToHistory(item.data)
                    }
                })
            }

            const source = result.source || 'backend'
            const successCount = result.total || result.data?.summary?.successful || result.qrCodes?.length || 0
            const totalCount = batchItems.value.length

            toast.success(`Batch generated: ${successCount}/${totalCount} successful (${source})`)
            return result

        } catch (error) {
            console.error('Batch generation error:', error)
            toast.error(error.message || 'Failed to generate batch QR codes')
            throw error
        } finally {
            isGenerating.value = false
        }
    }

    const generateWithQuickStyle = async (quickStyleType) => {
        if (!canGenerate.value) return

        // Map quick styles to proper style objects
        const quickStyles = {
            dots: { dotsType: 'dots' },
            rounded: { dotsType: 'rounded', cornersSquareType: 'extra-rounded' },
            gradient: { gradientColors: ['#ff6b6b', '#4ecdc4'], gradientType: 'linear' },
            modern: { preset: 'modern' },
            elegant: { preset: 'elegant' },
            minimal: { preset: 'minimal' }
        }

        const quickStyle = quickStyles[quickStyleType]
        if (!quickStyle) {
            toast.error(`Unknown quick style: ${quickStyleType}`)
            return
        }

        // Temporarily apply quick style
        const originalEnabled = styleOptions.value.enabled
        const originalStyle = { ...styleOptions.value }

        styleOptions.value.enabled = true
        Object.assign(styleOptions.value, quickStyle)

        try {
            const result = await generateQR()
            toast.success(`${quickStyleType} style applied!`)
            return result
        } finally {
            // Restore original settings
            styleOptions.value.enabled = originalEnabled
            Object.assign(styleOptions.value, originalStyle)
        }
    }

    const regenerateWithFrontend = async () => {
        if (!currentQR.value) return

        const originalSource = qrOptions.value.source
        qrOptions.value.source = 'frontend'

        try {
            await generateQR()
            toast.success('Regenerated with frontend!')
        } finally {
            qrOptions.value.source = originalSource
        }
    }

    const regenerateWithBackend = async () => {
        if (!currentQR.value) return

        const originalSource = qrOptions.value.source
        qrOptions.value.source = 'backend'

        try {
            await generateQR()
            toast.success('Regenerated with backend!')
        } finally {
            qrOptions.value.source = originalSource
        }
    }

    const getContentForActiveTab = () => {
        switch (formData.value.activeTab) {
            case 'text':
                return formData.value.text
            case 'url':
                return formData.value.url
            case 'sms':
                return `SMSTO:${formData.value.sms.number}:${formData.value.sms.message}`
            case 'email':
                return `mailto:${formData.value.email.address}?subject=${encodeURIComponent(formData.value.email.subject)}&body=${encodeURIComponent(formData.value.email.body)}`
            case 'vcard':
                return formData.value.contact
            case 'wifi':
                return formData.value.wifi
            default:
                return ''
        }
    }

    const addToHistory = (qrData) => {
        const historyItem = {
            id: qrData.id || Date.now(),
            timestamp: new Date(),
            type: formData.value.activeTab,
            content: getContentForActiveTab(),
            qrCode: qrData.qrCode || qrData.qrCodeData,
            options: { ...qrOptions.value },
            style: styleOptions.value.enabled ? { ...currentStyle.value } : null,
            preset: styleOptions.value.preset || null,
            source: qrData.source || generationSource.value,
            metadata: {
                ...qrData.metadata,
                hasStyle: styleOptions.value.enabled || !!styleOptions.value.preset,
                styleType: styleOptions.value.type || styleOptions.value.preset || (styleOptions.value.dotsType !== 'square' ? 'advanced' : 'basic'),
                generationMethod: qrData.source === 'frontend' ? 'frontend' : 'backend',
                libraryVersion: qrData.source === 'frontend' ? 'qr-code-styling' : 'qrcode'
            }
        }

        generationHistory.value.unshift(historyItem)

        if (generationHistory.value.length > 100) {
            generationHistory.value = generationHistory.value.slice(0, 100)
        }

        saveHistoryToStorage()
    }

    const saveHistoryToStorage = () => {
        try {
            localStorage.setItem('qrHistory', JSON.stringify(generationHistory.value))
        } catch (error) {
            console.error('Failed to save history to localStorage:', error)
        }
    }

    const loadHistoryFromStorage = () => {
        try {
            const saved = localStorage.getItem('qrHistory')
            if (saved) {
                generationHistory.value = JSON.parse(saved)
            }
        } catch (error) {
            console.error('Failed to load history from localStorage:', error)
            generationHistory.value = []
        }
    }

    const clearHistory = () => {
        generationHistory.value = []
        localStorage.removeItem('qrHistory')
        toast.success('History cleared successfully!')
    }

    // Preferences management
    const loadPreferences = () => {
        try {
            const saved = localStorage.getItem('qrPreferences')
            if (saved) {
                Object.assign(preferences.value, JSON.parse(saved))
            }
        } catch (error) {
            console.error('Failed to load preferences:', error)
        }
    }

    const savePreferences = () => {
        try {
            localStorage.setItem('qrPreferences', JSON.stringify(preferences.value))
        } catch (error) {
            console.error('Failed to save preferences:', error)
        }
    }

    const updatePreferences = (newPreferences) => {
        Object.assign(preferences.value, newPreferences)
        savePreferences()
    }

    const updateQROptions = (newOptions) => {
        Object.assign(qrOptions.value, newOptions)
    }

    const updateStyleOptions = (newStyleOptions) => {
        Object.assign(styleOptions.value, newStyleOptions)
    }

    const resetQROptions = () => {
        qrOptions.value = {
            size: 300,
            margin: 10,
            errorCorrectionLevel: 'M',
            darkColor: '#000000',
            lightColor: '#ffffff',
            format: 'png',
            source: 'auto'
        }
    }

    const resetStyleOptions = () => {
        styleOptions.value = {
            enabled: false,
            preset: null,
            dotsType: 'square',
            cornersSquareType: 'square',
            cornersDotType: 'square',
            darkColor: null,
            lightColor: null,
            backgroundColor: null,
            gradientColors: [],
            gradientType: 'linear',
            gradientRotation: 0,
            type: null,
            dotScale: 0.8,
            cornerRadius: 20,
            iconStyle: 'square',
            iconScale: 0.4,
            logoScale: 0.4,
            background: null
        }
    }

    const applyPreset = (presetName) => {
        styleOptions.value.enabled = true
        styleOptions.value.preset = presetName

        // Clear other style options when applying preset
        styleOptions.value.type = null
        styleOptions.value.dotsType = 'square'
        styleOptions.value.cornersSquareType = 'square'
        styleOptions.value.gradientColors = []

        toast.success(`Applied ${presetName} preset`)
    }

    const regenerateFromHistory = (item) => {
        formData.value.activeTab = item.type

        switch (item.type) {
            case 'text':
                formData.value.text = item.content
                break
            case 'url':
                formData.value.url = item.content
                break
            case 'vcard':
                formData.value.contact = { ...item.content }
                break
            case 'wifi':
                formData.value.wifi = { ...item.content }
                break
            case 'sms':
                const smsMatch = item.content.match(/SMSTO:([^:]+):(.*)/)
                if (smsMatch) {
                    formData.value.sms.number = smsMatch[1]
                    formData.value.sms.message = smsMatch[2]
                }
                break
            case 'email':
                const emailMatch = item.content.match(/mailto:([^?]+)\?subject=([^&]*)&body=(.*)/)
                if (emailMatch) {
                    formData.value.email.address = emailMatch[1]
                    formData.value.email.subject = decodeURIComponent(emailMatch[2])
                    formData.value.email.body = decodeURIComponent(emailMatch[3])
                }
                break
        }

        // Restore options and styles
        updateQROptions(item.options || {})

        if (item.style) {
            styleOptions.value.enabled = true
            if (item.preset) {
                applyPreset(item.preset)
            } else {
                styleOptions.value.preset = null
                Object.assign(styleOptions.value, item.style)
            }
        } else {
            styleOptions.value.enabled = false
        }

        currentQR.value = {
            ...item,
            qrCode: item.qrCode
        }
    }

    const deleteFromHistory = async (id) => {
        const index = generationHistory.value.findIndex(item => item.id === id)
        if (index !== -1) {
            const item = generationHistory.value[index]

            // Delete from server if it has a server ID
            if (item.id && typeof item.id === 'string' && item.id.length > 10) {
                try {
                    await QRService.deleteQR(item.id)
                } catch (error) {
                    console.warn('Failed to delete from server:', error)
                }
            }

            generationHistory.value.splice(index, 1)
            saveHistoryToStorage()
            toast.success('Item removed from history')
        }
    }

    const downloadQR = async (format = 'png') => {
        if (!currentQR.value) {
            toast.error('No QR code to download')
            return
        }

        try {
            if (currentQR.value.id && !currentQR.value.immediate) {
                await QRService.downloadQR(currentQR.value.id, format)
            } else {
                // Download from data URL
                const link = document.createElement('a')
                link.download = `qrcode-${Date.now()}.${format}`
                link.href = currentQR.value.qrCode || currentQR.value.qrCodeData
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            }
            toast.success('QR code downloaded successfully!')
        } catch (error) {
            console.error('Download error:', error)
            toast.error('Failed to download QR code')
        }
    }

    const loadMyQRs = async (filters = {}) => {
        try {
            const result = await QRService.getMyQRs(filters)
            return result.data || result
        } catch (error) {
            toast.error('Failed to load QR codes')
            throw error
        }
    }

    const loadAnalytics = async (timeframe = '30d', source = null) => {
        isLoadingAnalytics.value = true
        try {
            const result = await QRService.getAnalytics(timeframe, source)
            analytics.value = result.data || result
            return analytics.value
        } catch (error) {
            toast.error('Failed to load analytics')
            throw error
        } finally {
            isLoadingAnalytics.value = false
        }
    }

    const loadUsageAnalytics = async (timeframe = '30d') => {
        try {
            const result = await QRService.getUsageAnalytics(timeframe)
            usageAnalytics.value = result.data || result
            return usageAnalytics.value
        } catch (error) {
            toast.error('Failed to load usage analytics')
            throw error
        }
    }

    const checkServiceStatus = async () => {
        try {
            const status = await QRService.getServiceStatus()
            serviceStatus.value = status
            return status
        } catch (error) {
            console.error('Failed to check service status:', error)
            serviceStatus.value = { error: error.message }
            return serviceStatus.value
        }
    }

    const resetForm = () => {
        formData.value = {
            activeTab: 'text',
            text: '',
            contact: {
                name: '',
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                organization: '',
                website: '',
                address: '',
                title: ''
            },
            wifi: {
                ssid: '',
                password: '',
                security: 'WPA',
                hidden: false
            },
            url: '',
            sms: {
                number: '',
                message: ''
            },
            email: {
                address: '',
                subject: '',
                body: ''
            }
        }

        logoFile.value = null
        iconFile.value = null
        currentQR.value = null
        batchItems.value = []
        resetStyleOptions()
    }

    // File handling
    const setLogoFile = (file) => {
        logoFile.value = file
        iconFile.value = null // Clear icon if logo is set
    }

    const setIconFile = (file) => {
        iconFile.value = file
        logoFile.value = null // Clear logo if icon is set
    }

    const removeLogoFile = () => {
        logoFile.value = null
    }

    const removeIconFile = () => {
        iconFile.value = null
    }

    // Batch management
    const addToBatch = (item) => {
        batchItems.value.push({
            id: Date.now(),
            text: item.text,
            style: item.style || null,
            options: item.options || {},
            ...item
        })
    }

    const removeFromBatch = (index) => {
        batchItems.value.splice(index, 1)
    }

    const clearBatch = () => {
        batchItems.value = []
    }

    const duplicateFromHistory = (item) => {
        addToBatch({
            text: item.content,
            style: item.style,
            options: item.options,
            contentType: item.type
        })
        toast.success('Added to batch')
    }

    // Initialize
    loadHistoryFromStorage()
    checkServiceStatus()

    return {
        // State
        currentQR,
        isGenerating,
        generationHistory,
        vcardValidationErrors,
        sessionId,
        qrOptions,
        styleOptions,
        preferences,
        formData,
        logoFile,
        iconFile,
        batchItems,
        analytics,
        usageAnalytics,
        isLoadingAnalytics,
        serviceStatus,

        // Computed
        hasContent,
        canGenerate,
        currentStyle,
        shouldUseFrontend,
        availablePresets,
        generationSource,

        // Actions
        generatePreview,
        initializeSession,
        generateQR,
        generateBatch,
        generateWithQuickStyle,
        regenerateWithFrontend,
        regenerateWithBackend,
        addToHistory,
        clearHistory,
        regenerateFromHistory,
        loadHistoryFromStorage,
        deleteFromHistory,
        downloadQR,
        resetForm,
        updateQROptions,
        updateStyleOptions,
        updatePreferences,
        resetQROptions,
        resetStyleOptions,
        applyPreset,
        setLogoFile,
        setIconFile,
        removeLogoFile,
        removeIconFile,
        getContentForActiveTab,
        loadMyQRs,
        loadAnalytics,
        loadUsageAnalytics,
        checkServiceStatus,
        addToBatch,
        removeFromBatch,
        clearBatch,
        duplicateFromHistory,

        // Legacy aliases for backward compatibility
        loadStyleAnalytics: loadAnalytics,
        styleAnalytics: analytics
    }
})